import React, { useState, useEffect } from "react";
// import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
// import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Papa from "papaparse";
import "./DownloadCatalogues.css";

const SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbzJYFBgsdhes41k5I8mjvTBDCnqIATz_P_EGlAdx-8wRyFY5G3GSZrguR9ZcUNf86Mp/exec";
// const RECAPTCHA_SITE_KEY = "6LdG4tcrAAAAACrpa9DG-_S4Jv4SFph_gFP4N8rj";

// ------------------------------
// Validation Schema
// ------------------------------
const validationSchema = yup.object().shape({
  from_name: yup.string().required("Your name is required."),
  from_email: yup.string().email("Please enter a valid email.").required("Email required"),
  from_phone: yup.string().required("Phone required"),
  message: yup.string().required("Message required"),
  // recaptcha: yup.string().required("Verify captcha"),
});

// ------------------------------
// MAIN COMPONENT
// ------------------------------
const DownloadCatalogues = () => {
  const [step, setStep] = useState("form");
  const [userData, setUserData] = useState(null);
  const [catalogueList, setCatalogueList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  // ------------------------------
  // Fetch catalogues dynamically
  // ------------------------------
  useEffect(() => {
    const GOOGLE_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQd1CoMIcSXJsHiuFkAmaX6q1a17rP2ux4OoLuUFUdgKKTPfKWPVnxoRJ3fy31C1eRpWLmd8vI3SdFl/pub?output=csv";

    Papa.parse(GOOGLE_SHEET_URL, {
      download: true,
      header: true,
      complete: (results) => {
        const validData = results.data.filter(row => row.id && row.id.trim() !== "");
        setCatalogueList(validData);
        setIsLoading(false);
      },
      error: (err) => {
        console.error("Error fetching catalogue data:", err);
        setError("Could not load catalogues. Please try again later.");
        setIsLoading(false);
      },
    });
  }, []);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(validationSchema) });

  // ---------------------------------------
  // FIRST FORM SUBMIT → saves user info (no sheet entry, only when downloading)
  // ---------------------------------------
  const onSubmit = async (data) => {
    setStatusMessage("Submitting...");
    
    // Save user data and proceed directly without captcha verification
    setStatusMessage("Success! Loading catalogues...");
    setUserData(data);
    reset();
    
    // Try to log form submission (non-blocking - don't wait for response)
    try {
      const payload = {
        name: data.from_name,
        email: data.from_email,
        phone: data.from_phone,
        message: data.message,
        catalogueName: "Form Submission",
        recaptchaToken: "",
      };

      fetch(SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      }).catch(err => {
        // Silently fail - non-critical operation
        console.log("Form submission log failed:", err);
      });
    } catch (error) {
      // Silently fail - non-critical operation
      console.log("Form submission error:", error);
    }

    setTimeout(() => {
      setStep("catalogues");
      setStatusMessage("");
    }, 1500);
  };

  // ---------------------------------------
  // DOWNLOAD LOGIC (logs each catalogue)
  // ---------------------------------------
  const handleCatalogueDownload = async (item) => {
    if (!item.pdfUrl) {
      alert("PDF URL not available for this catalogue.");
      return;
    }

    // Set loading state for this specific catalogue
    setDownloadingId(item.id);

    const payload = {
      name: userData.from_name,
      email: userData.from_email,
      phone: userData.from_phone,
      message: userData.message,
      catalogueName: item.title || "Unknown Catalogue",
      recaptchaToken: "",
    };

    try {
      // Log download to Google Sheets
      await fetch(SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      });

      // Small delay to ensure user sees the loading state
      await new Promise(resolve => setTimeout(resolve, 300));

      // Trigger browser download
      const link = document.createElement("a");
      link.href = item.pdfUrl;
      link.setAttribute("download", item.pdfUrl.split("/").pop() || `${item.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Reset loading state after download starts
      setTimeout(() => {
        setDownloadingId(null);
      }, 1000);
    } catch (error) {
      console.error("Error downloading catalogue:", error);
      alert("Failed to log download. Please try again.");
      setDownloadingId(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="download-page">
        <p className="status-message">Loading catalogues...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="download-page">
        <p className="status-message error">{error}</p>
      </div>
    );
  }

  return (
    <div className="download-page">
      {step === "form" && (
        <div>
          <div className="download-header">
            <h2>Download Our Catalogues</h2>
            <p>Please fill the form to continue</p>
          </div>

          <form className="download-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group">
              <input
                {...register("from_name")}
                placeholder="Your Name"
                type="text"
              />
              {errors.from_name && (
                <span className="error-message">{errors.from_name.message}</span>
              )}
            </div>

            <div className="form-group">
              <input
                {...register("from_email")}
                placeholder="Email"
                type="email"
              />
              {errors.from_email && (
                <span className="error-message">{errors.from_email.message}</span>
              )}
            </div>

            <div className="form-group">
              <input
                {...register("from_phone")}
                placeholder="Phone"
                type="tel"
              />
              {errors.from_phone && (
                <span className="error-message">{errors.from_phone.message}</span>
              )}
            </div>

            <div className="form-group">
              <textarea
                {...register("message")}
                placeholder="Message / Inquiry"
                rows="4"
              />
              {errors.message && (
                <span className="error-message">{errors.message.message}</span>
              )}
            </div>

            {/* <div className="form-group">
              <Controller
                name="recaptcha"
                control={control}
                render={({ field }) => (
                  <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={field.onChange} />
                )}
              />
              {errors.recaptcha && (
                <span className="error-message">{errors.recaptcha.message}</span>
              )}
            </div> */}

            <button type="submit" className="download-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit & Continue"}
            </button>

            {statusMessage && (
              <p className="status-message" style={{ marginTop: "1rem", textAlign: "center" }}>
                {statusMessage}
              </p>
            )}
          </form>
        </div>
      )}

      {step === "catalogues" && (
        <div className="catalogues-section-wrapper">
          <div className="catalogues-grid-title">
            <h2>Choose a Catalogue to Download</h2>
            <p>You can download all catalogues one by one.</p>
          </div>

          {catalogueList.length === 0 ? (
            <p className="status-message">No catalogues are available at this time.</p>
          ) : (
            <div className="catalogues-grid">
              {catalogueList.map((item) => (
                <div key={item.id} className="catalogue-card">
                  {item.coverImageUrl && (
                    <div className="catalogue-card-image">
                      <img 
                        src={item.coverImageUrl} 
                        alt={item.title}
                        loading="lazy"
                      />
                    </div>
                  )}
                  <h4>{item.title}</h4>
                  {item.description && (
                    <p className="catalogue-card-description">{item.description}</p>
                  )}
                  <button
                    className="catalogue-download-btn"
                    onClick={() => handleCatalogueDownload(item)}
                    disabled={downloadingId === item.id}
                  >
                    {downloadingId === item.id ? (
                      <span className="download-loader">
                        <span className="loader-spinner"></span>
                        Downloading...
                      </span>
                    ) : (
                      "Download"
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DownloadCatalogues;
